<template>
  <!-- CustomMultiSelect is the component behind every dropdown
       filter in the sidebar — collapsed by default showing
       defaultText ("Tutti"/"Tutte"), expands to a checklist with search box past 10 options. -->
  <custom-multi-select
    :options="checkboxOptions"
    :value="checkedValues"
    :default-text="$t('common.allMale')"
    show-all
    use-ticks
    :select-all-text="$t('common.selectAllMale')"
    show-clear-selection
    :show-count="showCount"
    multiple
    maintain-original-order
    :placeholder="$t('common.search')"
    @input="onInput"
  ></custom-multi-select>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import CustomMultiSelect from '../../Lib/CustomMultiSelect'

const { mapGetters } = createNamespacedHelpers('search')

// Generic MultiSelect filter. Two option shapes exist in the catalog:
// - string codes (most filters) — value is the array of selected codes.
// - {from,to} interval objects (TMTimeHorizonIntervals, AdditionalInfo.RedeemTermIntervals) — value
//   is the array of selected interval objects, matched by index since intervals have no stable code.
export default {
  name: 'multiSelectFilter',
  components: { CustomMultiSelect },
  props: {
    filterKey: { type: String, required: true },
    options: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
    // The Followed page's filters never show the selected-count
    // badge (unlike the search sidebar, which keeps it). Defaults to true everywhere else.
    showCount: { type: Boolean, default: true },
  },
  computed: {
    ...mapGetters(['issuerName']),
    isIntervalOptions() {
      return this.options.length > 0 && typeof this.options[0] === 'object'
    },
    checkboxOptions() {
      if (this.isIntervalOptions) {
        // CustomMultiSelect stringifies the v-model array internally (`_value`) but leaves option
        // values as-is — string indices avoid a type-mismatch that would make selections never show.
        return this.options.map((interval, index) => ({ value: String(index), text: this.intervalLabel(interval, index) }))
      }
      // IssuerCodes' dynamic options are plain issuer codes (see FilterValuesResolver on the BE) —
      // resolve to the issuer name, via the code→name map (loaded once from the
      // /api/searchfilters/values endpoint).
      if (this.filterKey === 'IssuerCodes') {
        return this.sortByText(this.options.map((code) => ({ value: code, text: this.issuerName(code) })))
      }
      return this.sortByText(this.options.map((code) => ({ value: code, text: this.optionLabel(code) })))
    },
    checkedValues() {
      if (!this.isIntervalOptions) {
        return this.value || []
      }
      return (this.value || []).map((interval) =>
        String(this.options.findIndex((o) => o.from === interval.from && o.to === interval.to))
      )
    },
  },
  methods: {
    // The catalog only carries raw codes (no labels), so alphanumeric ordering can only happen here,
    // after the code is resolved to its displayed text — the BE returns codes in enum-declaration
    // order, not sorted. Interval-based options (bucket order) never go through this path.
    sortByText(opts) {
      return [...opts].sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' }))
    },
    optionLabel(code) {
      const key = 'search.options.' + this.filterKey + '.' + code
      return this.$te(key) ? this.$t(key) : code
    },
    intervalLabel(interval, index) {
      // The `timeHorizon` templates (it.json), filled in with the REAL RHP bucket boundaries —
      // these intervals come from the backend (dynamic:companies.time_horizon_interval), in a fixed
      // bucket order (short/short-medium/medium/long), so index 0-3 maps 1:1 to bucket ids
      // 1/2/4/8. The last bucket's lower
      // bound is shown as "+1" (`RHP >= {n}`), matching main.js's special case for id 8.
      if (this.filterKey === 'TMTimeHorizonIntervals') {
        const codes = ['1', '2', '4', '8']
        const code = codes[index]
        const n = code === '8' ? interval.from + 1 : interval.from
        return this.$t('search.filterSelectValues.timeHorizon.' + code, { n, z: interval.to })
      }
      // The `aiRedeemTerm` value map (it.json) — codes 0-3, where "0"/"Assente" is the
      // {from:0,to:0} sentinel (RedeemTerm is null/unset), not a real "0 - 0" interval.
      if (this.filterKey === 'AdditionalInfo.RedeemTermIntervals') {
        return this.$t('search.filterSelectValues.DIP.additionalInformation.aiRedeemTerm.' + index)
      }
      if (interval.from === null || interval.from === undefined) return '< ' + interval.to
      if (interval.to === null || interval.to === undefined) return '> ' + interval.from
      return interval.from + ' - ' + interval.to
    },
    onInput(selected) {
      if (!this.isIntervalOptions) {
        this.$emit('input', selected)
        return
      }
      this.$emit('input', selected.map((index) => this.options[Number(index)]))
    },
  },
}
</script>
